import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.requires_verification) {
        toast.info('Please verify your email first');
        navigate('/verify-email', { state: { email, redirectTo: '/browse' } });
      } else {
        toast.success('Welcome back!');
        navigate('/browse');
      }
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030014] flex items-center justify-center px-4" data-testid="login-page">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1565590915406-9c386cc9391d?w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/60" />
      </div>

      {/* Logo */}
      <Link to="/" className="absolute top-6 left-6 z-10">
        <img src="/reframes-logo.png" alt="reframes" className="h-9 md:h-11" />
      </Link>

      {/* Auth Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="admin-card p-8">
          <h1
            className="text-2xl md:text-3xl font-bold mb-2 text-center"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Welcome Back
          </h1>
          <p className="text-gray-400 text-center mb-8">
            Sign in to continue watching
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12"
                data-testid="email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="pl-10 pr-10 bg-[#030014] border-[#8B5CF6]/20 h-12"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-base"
              data-testid="submit-button"
            >
              {loading ? (
                <div className="spinner w-5 h-5" />
              ) : (
                'Sign In'
              )}
            </Button>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[#8B5CF6] hover:text-[#A78BFA] transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0F0B1E] text-gray-400">or</span>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={loginWithGoogle}
            className="w-full btn-secondary h-12 flex items-center justify-center gap-3"
            data-testid="google-signin-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          {/* Sign Up Links */}
          <div className="mt-8 space-y-4">
            <p className="text-center text-gray-400">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#8B5CF6] hover:text-[#A78BFA] font-medium">
                Sign Up
              </Link>
            </p>
            
            <div className="pt-4 border-t border-white/10 text-center">
              <Link
                to="/creator/register"
                className="inline-flex items-center gap-2 text-sm text-[#00FF9D] hover:underline"
                data-testid="creator-register-link"
              >
                <Building2 className="w-4 h-4" />
                Register as a Creator
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
